/**
 * PostHog Events for Narrator Loading System
 *
 * Track narrator display states, streaming handoff, and user engagement.
 *
 * Analytics Anti-Pattern Compliance:
 * - ✅ All event names are English constants (NO t() translations)
 * - ✅ All property values are English constants or primitives
 * - ✅ User-facing text uses t() separately (not in analytics)
 *
 * Event Flow:
 * 1. Narrator_Loading_Started → analysis begins
 * 2. Narrator_Quip_Displayed → each quip shown
 * 3. Narrator_Quip_Cycled → quip changes during loading
 * 4. Narrator_Streaming_Started → LLM response begins
 * 5. Narrator_Streaming_Complete → full narrator received
 * 6. Narrator_Fallback_Triggered → streaming failed, using summary
 * 7. Narrator_Display_Complete → final display state
 *
 * Phase 5: Metrics & Rollout for Narrator Loading System
 * Created: 2026-02-03
 */

import { captureEvent } from '../../analytics';

// ============================================================================
// Event Name Constants (English only, no translations)
// ============================================================================

export const NARRATOR_EVENTS = {
  // Loading phase events
  LOADING_STARTED: 'Narrator_Loading_Started',
  QUIP_DISPLAYED: 'Narrator_Quip_Displayed',
  QUIP_CYCLED: 'Narrator_Quip_Cycled',

  // Streaming handoff events
  STREAMING_STARTED: 'Narrator_Streaming_Started',
  STREAMING_COMPLETE: 'Narrator_Streaming_Complete',
  STREAMING_FAILED: 'Narrator_Streaming_Failed',
  STREAMING_TIMEOUT: 'Narrator_Streaming_Timeout',

  // Fallback events
  FALLBACK_TRIGGERED: 'Narrator_Fallback_Triggered',

  // Completion events
  DISPLAY_COMPLETE: 'Narrator_Display_Complete',

  // Engagement events
  NARRATOR_VISIBLE_DURATION: 'Narrator_Visible_Duration',

  // Feature flag events
  FEATURE_FLAGS_EVALUATED: 'Narrator_Feature_Flags_Evaluated',

  // Score chase reaction events
  REACTION_SHOWN: 'Narrator_Reaction_Shown',
  PILL_SHOWN: 'Narrator_Pill_Shown',
  PILL_TAPPED: 'Narrator_Pill_Tapped',
  SESSION_SUMMARY: 'Narrator_Session_Summary',
} as const;

// ============================================================================
// Event Property Types (TypeScript for type safety)
// ============================================================================

/**
 * Base properties shared across all narrator events
 */
interface NarratorBaseProperties {
  /** User's subscription tier */
  tier: 'anonymous' | 'free' | 'drama_plus' | 'dramax';
  /** Voice style for the tier (playful/balanced/professional) */
  voiceStyle: 'playful' | 'balanced' | 'professional';
  /** Session ID for correlation (optional) */
  sessionId?: string;
}

/**
 * Properties for Narrator_Loading_Started event
 */
interface LoadingStartedProperties extends NarratorBaseProperties {
  /** Theatrical mode based on tier */
  theatricalMode: 'quick' | 'theatrical' | 'vip';
}

/**
 * Properties for Narrator_Quip_Displayed and Narrator_Quip_Cycled events
 */
interface QuipDisplayedProperties extends NarratorBaseProperties {
  /** Unique quip identifier (English translation key) */
  quipId: string;
  /** Index of quip in the tier's quip array */
  quipIndex: number;
  /** How long this quip was displayed (ms) */
  displayDurationMs: number;
  /** Whether i18n translation function was available (true = translated, false = hardcoded fallback) */
  translationAvailable: boolean;
}

/**
 * Properties for quip cycling (extends QuipDisplayedProperties)
 */
interface QuipCycledProperties extends QuipDisplayedProperties {
  /** Previous quip ID before cycling */
  previousQuipId: string;
}

/**
 * Properties for streaming events
 */
interface StreamingProperties extends NarratorBaseProperties {
  /** Time from analysis start to first chunk (ms) */
  timeToFirstChunkMs?: number;
  /** Total time spent streaming (ms) */
  totalStreamDurationMs?: number;
  /** Final character count of narrator_intro */
  finalTextLength?: number;
  /** Whether fallback was used instead of streaming */
  didFallback?: boolean;
}

/**
 * Properties for streaming failure events
 */
interface StreamingFailedProperties extends StreamingProperties {
  /** Error message (scrubbed of PII) */
  errorMessage?: string;
}

/**
 * Properties for fallback events
 */
interface FallbackProperties extends NarratorBaseProperties {
  /** Why fallback was triggered */
  reason: 'timeout' | 'error' | 'no_narrator';
  /** Number of quips shown before fallback */
  quipsShownCount: number;
  /** Time elapsed before fallback (ms) */
  timeBeforeFallbackMs: number;
}

/**
 * Properties for display completion events
 */
interface DisplayCompleteProperties extends NarratorBaseProperties {
  /** Source of final display text */
  source: 'loading_quip' | 'streamed' | 'final' | 'fallback';
  /** Total time from start to display complete (ms) */
  totalDisplayTimeMs: number;
  /** Number of quips shown during loading */
  quipsShownCount: number;
  /** Whether streaming was used */
  didStream: boolean;
}

/**
 * Properties for visible duration tracking
 */
interface VisibleDurationProperties {
  /** User's subscription tier */
  tier: string;
  /** Duration narrator was visible (ms) */
  durationMs: number;
  /** Final state when narrator became invisible */
  state: string;
}

/**
 * Properties for feature flags evaluated event
 */
interface FeatureFlagsEvaluatedProperties {
  /** Loading quips enabled flag value */
  loading_quips_enabled: boolean;
  /** Streaming handoff enabled flag value */
  streaming_handoff_enabled: boolean;
  /** Display v2 enabled flag value */
  display_v2_enabled: boolean;
  /** Quip interval override value (undefined for tier defaults) */
  quip_interval_override?: number;
}

// ============================================================================
// Event Capture Functions (Type-Safe API)
// ============================================================================

/**
 * Capture Narrator_Loading_Started event
 *
 * Fired when analysis begins and narrator loading state machine starts.
 *
 * @param properties - Loading started properties
 */
export function captureNarratorLoadingStarted(
  properties: LoadingStartedProperties
): void {
  captureEvent(NARRATOR_EVENTS.LOADING_STARTED, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Quip_Displayed event
 *
 * Fired when a new loading quip is shown to the user.
 *
 * @param properties - Quip displayed properties
 */
export function captureNarratorQuipDisplayed(
  properties: QuipDisplayedProperties
): void {
  captureEvent(NARRATOR_EVENTS.QUIP_DISPLAYED, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Quip_Cycled event
 *
 * Fired when cycling from one quip to another during loading.
 *
 * @param properties - Quip cycled properties (includes previousQuipId)
 */
export function captureNarratorQuipCycled(
  properties: QuipCycledProperties
): void {
  captureEvent(NARRATOR_EVENTS.QUIP_CYCLED, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Streaming_Started event
 *
 * Fired when LLM begins streaming narrator_intro content.
 *
 * @param properties - Streaming properties
 */
export function captureNarratorStreamingStarted(
  properties: StreamingProperties
): void {
  captureEvent(NARRATOR_EVENTS.STREAMING_STARTED, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Streaming_Complete event
 *
 * Fired when streaming successfully completes.
 *
 * @param properties - Streaming properties (includes finalTextLength)
 */
export function captureNarratorStreamingComplete(
  properties: StreamingProperties
): void {
  captureEvent(NARRATOR_EVENTS.STREAMING_COMPLETE, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Streaming_Failed event
 *
 * Fired when streaming encounters an error.
 *
 * @param properties - Streaming failed properties (includes errorMessage)
 */
export function captureNarratorStreamingFailed(
  properties: StreamingFailedProperties
): void {
  captureEvent(NARRATOR_EVENTS.STREAMING_FAILED, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Streaming_Timeout event
 *
 * Fired when streaming times out (no chunks received within threshold).
 *
 * @param properties - Streaming properties
 */
export function captureNarratorStreamingTimeout(
  properties: StreamingProperties
): void {
  captureEvent(NARRATOR_EVENTS.STREAMING_TIMEOUT, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Fallback_Triggered event
 *
 * Fired when fallback text (summary) is used instead of narrator_intro.
 *
 * @param properties - Fallback properties
 */
export function captureNarratorFallbackTriggered(
  properties: FallbackProperties
): void {
  captureEvent(NARRATOR_EVENTS.FALLBACK_TRIGGERED, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Display_Complete event
 *
 * Fired when narrator reaches final display state.
 *
 * @param properties - Display complete properties
 */
export function captureNarratorDisplayComplete(
  properties: DisplayCompleteProperties
): void {
  captureEvent(NARRATOR_EVENTS.DISPLAY_COMPLETE, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Visible_Duration event
 *
 * Fired when narrator component unmounts or becomes invisible.
 * Tracks how long users actually read the narrator.
 *
 * @param tier - User's subscription tier
 * @param durationMs - Duration narrator was visible (ms)
 * @param state - Final narrator state when visibility ended
 */
export function captureNarratorVisibleDuration(
  tier: string,
  durationMs: number,
  state: string
): void {
  captureEvent(NARRATOR_EVENTS.NARRATOR_VISIBLE_DURATION, {
    tier,
    durationMs,
    state,
    timestamp: new Date().toISOString(),
  });
}

/**
 * Capture Narrator_Feature_Flags_Evaluated event
 *
 * Fired when narrator feature flags are evaluated by PostHog.
 * Tracks which flags are enabled for analytics and debugging.
 *
 * @param properties - Feature flags evaluated properties
 */
export function captureNarratorFeatureFlagsEvaluated(
  properties: FeatureFlagsEvaluatedProperties
): void {
  captureEvent(NARRATOR_EVENTS.FEATURE_FLAGS_EVALUATED, {
    ...properties,
    timestamp: new Date().toISOString(),
  });
}

// Score chase reaction events — extracted to narrator-score-chase-events.ts
export {
  captureNarratorReactionShown,
  captureNarratorPillShown,
  captureNarratorPillTapped,
  captureNarratorSessionSummary,
} from './narrator-score-chase-events';
